// Pure helpers for the PQ-tuning dashboard surface. No React, no DOM —
// the badge component renders a variant produced here, and a unit test
// can exercise every branch without any framework setup.
//
// See plan: docs/plans/2026-05-15-feat-pq-tuning-dashboard-plan.md
// §"Visibility + styling" for the decision table the appearance function
// implements.

import type { Service } from "@/lib/types";

// Severity palette from web/dyno-pwa/src/components/PqActivity.tsx (the
// existing swimlane). Matching keeps the badge color semantically
// consistent with what operators already see elsewhere.
export const SEVERITY_COLORS: Record<string, string> = {
  severe: "#ef4444",
  moderate: "#fb923c",
  minor: "#facc15",
  none: "#94a3b8",
};

// SEVERITY VOCAB TRANSLATION — pq_events.severity holds BOTH vocabularies in
// production: 'critical'/'major'/'minor' from the COMTRADE datapost path
// (pq_parser.derive_severity) and 'severe'/'moderate'/'minor' from the
// fastlog MQTT path (mqtt_handlers_pq._classify_severity, the only writer
// for Acuvim II-R meters). Every read site must translate through this one
// map — comparing a raw DB value against a dashboard key silently drops one
// vocabulary. Per the 2026-05-26 data-hygiene audit §2.1 + 2026-06-10
// re-audit A1-A3.
export type DashboardSeverity = "minor" | "moderate" | "severe";
export const DB_TO_DASHBOARD_SEVERITY: Record<string, DashboardSeverity> = {
  critical: "severe",
  major:    "moderate",
  minor:    "minor",
  // fastlog rows already carry dashboard vocab — pass through
  severe:   "severe",
  moderate: "moderate",
};

/** Translate a raw pq_events.severity value to the dashboard vocabulary;
 * null/unknown values map to "uncategorized". */
export function dashboardSeverity(
  dbSeverity: string | null | undefined,
): DashboardSeverity | "uncategorized" {
  return DB_TO_DASHBOARD_SEVERITY[dbSeverity ?? ""] ?? "uncategorized";
}

// Slate = "quiet hint" — used for the downward-flag case (iNominal too
// high for current load; operator is missing inrush events but not
// flooding the system). Plan §"Visibility" calls this the muted treatment.
const SLATE = "#94a3b8";

// Amber = actionable config warning. The upward flag means sustained load
// is running well above the meter's iNominal reference, so PQ current
// capture is compromised until it's retuned. This is the highest-priority
// badge and renders at a FIXED prominent size + color: a meter flagged
// upward with zero events is the worst case — it can't produce events
// *because* iNominal is wrong — so the encoding must NOT scale down with
// event activity the way the noise badge does.
const UPWARD = "#fb923c";

export type BadgeAppearance =
  | { kind: "hidden" }
  | {
      kind: "noise";
      radius: number;
      color: string;
      pulse: "normal" | "fast";
    }
  | { kind: "upward"; radius: 9; color: typeof UPWARD }
  | { kind: "flood"; radius: 9; color: typeof UPWARD }
  | { kind: "downward"; radius: 5; color: typeof SLATE }
  | { kind: "syncing"; radius: 6 };

/** Log-scaled size that doesn't saturate at low event rates. */
export function radiusForEventRate(eventsLastHour: number): number {
  if (eventsLastHour <= 0) return 4;
  return Math.max(4, Math.min(14, 4 + Math.log(eventsLastHour + 1) * 3));
}

/** Pick the badge variant from the service's WS fields. The component
 * just renders whatever this returns.
 *
 * Visibility table (plan §"Visibility + styling"):
 *   inominal_status === 'syncing'    → syncing variant (always visible)
 *   flagged && reason='upward_noise' → upward variant (amber, fixed prominent
 *                                      size, pulse) — independent of events
 *   flagged && reason='event_flood'  → flood variant (amber, prominent, pulse)
 *                                      — too many PQ events to be healthy
 *   flagged && reason='downward_miss'→ downward variant (slate, fixed size)
 *   events_last_hour > 0             → noise variant, severity color, no pulse
 *   else                             → hidden
 */
export function deriveBadgeAppearance(s: Pick<
  Service,
  "inominal_status" | "flagged" | "reason" | "dominant_severity" | "events_last_hour"
>): BadgeAppearance {
  if (s.inominal_status === "syncing") {
    return { kind: "syncing", radius: 6 };
  }
  const events = s.events_last_hour ?? 0;
  if (s.flagged) {
    if (s.reason === "upward_noise") {
      // Fixed prominent size + amber color, deliberately NOT keyed to
      // events_last_hour or dominant_severity. A flagged-upward meter with
      // zero events is the highest-concern case (its iNominal is wrong, so
      // it can't even generate the events that would otherwise size the
      // badge) — encoding it as a tiny grey dot inverted the urgency.
      return { kind: "upward", radius: 9, color: UPWARD };
    }
    if (s.reason === "event_flood") {
      // A meter logging PQ events far faster than any healthy meter needs
      // operator attention regardless of where its sustained load sits — the
      // load-ratio variants would never catch it. Prominent + amber so it
      // reads as an actionable config warning, not passive event noise; the
      // dialog shows which way to retune iNominal.
      return { kind: "flood", radius: 9, color: UPWARD };
    }
    if (s.reason === "downward_miss") {
      return { kind: "downward", radius: 5, color: SLATE };
    }
  }
  if (events > 0) {
    const sev = s.dominant_severity ?? "none";
    return {
      kind: "noise",
      radius: radiusForEventRate(events),
      color: SEVERITY_COLORS[sev] || SEVERITY_COLORS.none,
      pulse: "normal",
    };
  }
  return { kind: "hidden" };
}

/** Status pill for the recalibrate result (used in dialog "syncing" banner). */
export function describeInominalStatus(status: Service["inominal_status"]): string {
  switch (status) {
    case "syncing":
      return "Recalibration in progress — meter offline ~1 min.";
    case "ok":
      return "Recalibration complete.";
    case "failed":
      return "Recalibration failed.";
    case "invalid":
      return "Meter rejected this value — likely out of CT-derived range.";
    default:
      return "";
  }
}
